import React, {
    useState,
    useEffect,
    useRef,
    useImperativeHandle,
    forwardRef,
} from 'react';
import PropTypes from 'prop-types';
import { Controlled as CodeMirror } from 'react-codemirror2';
import {
    isEqual as _isEqual,
    cloneDeep as _cloneDeep
} from 'lodash';

import { makeStyles, useTheme } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
// import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import ButtonGroup from '@mui/material/ButtonGroup';
import Checkbox from '@mui/material/Checkbox';
// import RadioGroup from '@mui/material/RadioGroup';
// import Radio from '@mui/material/Radio';

import Filter from './filter';
import Validator, { useValidate, checkValidity } from '../validator';

import SystemService from '../../data/system-service';

const useStyles = makeStyles(theme => ({
    fullWidth: {
        width: '100%',
    },
    timezone: {
        width: '120px',
    },
    hideFileInput: {
        display: 'none',
    },
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
    codemirrorBox: {
        display: 'block',
        width: '100%',
    },
    certBox: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(1),
        border: `1px solid ${theme.palette.primary.main}`,
        background: theme.styled.paper.background,
        color: theme.palette.text.primary,
    },
    codeContainer: {
        height: '120px',
        marginBottom: '-8px',
        '& .react-codemirror2': {
            height: '100%',
        },
        '& .CodeMirror': {
            height: '100%',
        }
    },
}));

const Zeek = forwardRef((props, ref) => {
    const classes = useStyles();
    const theme = useTheme();
    const [systemConstants, setSystemConstants] = useState({
        facilities: [],
        severities: [],
        timezones: [],
    });
    const filterRef = useRef(null);
    const [init, setInit] = useState(true);
    const [original, setOriginal] = useState(_cloneDeep(props.data));
    const [data, setData] = useState(_cloneDeep(props.data));
    const [, setChanges] = useState({});
    const dataForm = useValidate({});
    const [, setFormatRef] = useState(null);

    useImperativeHandle(ref, () => ({
        getData: () => {
            const f = filterRef.current.getFilter();
            return {
                ...data,
                filterconf: f,
            };
        },
    }));

    useEffect(() => {
        SystemService.getConstants().then((res) => {
            setSystemConstants(res);
        });
    }, []);

    useEffect(() => {
        const d = { ...props.data };
        if (d.prune_type === '') {
            d.prune_type = 'inactive';
        }
        if (init) {
            setOriginal(_cloneDeep(d));
            setData(_cloneDeep(d));
            setInit(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props]);

    useEffect(() => {
        const t = !_isEqual(data, original);
        setChanges(t);
        if (t) {
            props.onChange(props.name, t, data);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    useEffect(() => {
        if (props.onUpdateForm) {
            props.onUpdateForm(dataForm);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataForm]);

    const handleHost = (e, validID, valid) => {
        dataForm.set({ ...dataForm.get(), [validID]: valid });

        const d = { ...data };
        d.dest.host = e.target.value;
        setData(d);
    };

    const handlePort = (e, validID, valid) => {
        dataForm.set({ ...dataForm.get(), [validID]: valid });

        const d = { ...data };
        d.dest.port = e.target.value;
        setData(d);
    };

    const handleProtocol = (e, component) => {
        const d = { ...data };
        d.dest.proto = component.props.value;
        setData(d);
    };

    // const handlePruneTypeChange = (e) => {
    //     let d = {...data};
    //     d.prune_type = e.target.value;
    //     setData(d);
    // };

    // const handlePruneKeysChange = (e, validID, valid) => {
    //     dataForm.set({...dataForm.get(), [validID]: valid});

    //     let d = {...data};
    //     d.prune_keys = e.target.value ? e.target.value.replace(/\s/g, '').split(',') : [];
    //     setData(d);
    // };

    const handleCACert = (e) => {
        const d = { ...data };
        d.tls_cert.enable = e.currentTarget.checked;
        setData(d);
    };

    const fileHandler = (file) => {
        const reader = new FileReader();
        return new Promise((resolve, reject) => {
            reader.onload = event => resolve(event.target.result);
            reader.onerror = error => reject(error);
            reader.readAsText(file);
        });
    };

    const handleTLSContents = (e) => {
        fileHandler(e.target.files[0]).then(content => {
            SystemService.exportsCertAttributes(content).then((res) => {
                const d = { ...data };
                d.tls_cert.contents = content;
                d.tls_cert.file_name = `${res.subject} (${res.enddate})`;
                setData(d);
            }, (err) => {
                console.error(err);
            });
        }).catch(err => console.error(err));
    };

    const handleFIPS = (e) => {
        const d = { ...data };
        d.tls_fips = e.currentTarget.checked;
        setData(d);
    };

    const handleSetFormat = (format) => {
        const d = { ...data };
        if (format === 'empty') {
            d.bro.format = '';
        } else {
            d.bro.format = systemConstants.export_bro_format_templates[format];
        }
        setData(d);
    };

    const handleFormat = (e) => {
        const d = { ...data };
        d.bro.format = e;
        setData(d);
    };

    const handleUpdateFilter = (filter) => {
        setData({
            ...data,
            filterconf: filter,
        });
    };

    const handleVerify = (k, i, close) => {
        setInit(true);
        props.onVerify(k, i, close);
    };

    const handleCancel = (k, i) => {
        setInit(true);
        setData(original);
        props.onCancel(k, i);
    };

    const handleEventTypeAlerts = (e) => {
        const d = { ...data };
        if (e.currentTarget.checked) {
            d.event_types.push('alert');
        } else {
            d.event_types.splice(d.event_types.indexOf('alert'), 1);
        }
        setData(d);
    };

    const handleEventTypeMetadata = (e) => {
        const d = { ...data };
        if (e.currentTarget.checked) {
            d.event_types.push('bro_log');
        } else {
            d.event_types.splice(d.event_types.indexOf('bro_log'), 1);
        }
        setData(d);
    };

    const handleEventTypeDPIMetadata = (e) => {
        const d = { ...data };
        if (e.currentTarget.checked) {
            d.event_types.push('dpi');
        } else {
            d.event_types.splice(d.event_types.indexOf('dpi'), 1);
        }
        setData(d);
    };


    const handleEventTypeHealthAlerts = (e) => {
        const d = { ...data };
        if (e.currentTarget.checked) {
            d.event_types.push('health_alert');
        } else {
            d.event_types.splice(d.event_types.indexOf('health_alert'), 1);
        }
        setData(d);
    };

    const codemirrorOptions = {
        theme: theme.codeMirror.theme,
        lineWrapping: true,
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Box>
                    <Box display="inline-block" pr={5}>Event Type(s):</Box>
                    <FormControlLabel
                        label="Alerts"
                        onChange={(e) => { handleEventTypeAlerts(e) }}
                        control={<Checkbox disabled={(!props.perms.write || props.policySource === 'mc2')} checked={data.event_types.indexOf('alert') !== -1} />}
                    />
                    <FormControlLabel
                        label="Metadata"
                        onChange={(e) => { handleEventTypeMetadata(e) }}
                        control={<Checkbox disabled={(!props.perms.write || props.policySource === 'mc2')} checked={data.event_types.indexOf('bro_log') !== -1} />}
                    />
                    <FormControlLabel
                        label="DPI Metadata"
                        onChange={(e) => { handleEventTypeDPIMetadata(e) }}
                        control={<Checkbox disabled={(!props.perms.write || props.policySource === 'mc2')} checked={data.event_types.indexOf('dpi') !== -1} />}
                    />
                    <FormControlLabel
                        label="Health Alerts"
                        onChange={(e) => { handleEventTypeHealthAlerts(e) }}
                        control={<Checkbox disabled={(!props.perms.write || props.policySource === 'mc2')} checked={data.event_types.indexOf('health_alert') !== -1} />}
                    />
                </Box>
            </Grid>
            <Grid item xs={6}>
                <Box>
                    <FormControl className={classes.fullWidth}>
                        <FormLabel id={'zeek-protocol-label-' + props.index}>Protocol</FormLabel>
                        <Select disabled={(!props.perms.write || props.policySource === 'mc2')} id={'zeek-protocol-label-' + props.index} value={data.dest.proto} onChange={handleProtocol}>
                            <MenuItem value="UDP">UDP</MenuItem>
                            <MenuItem value="TCP">TCP</MenuItem>
                            <MenuItem value="TLS">TLS</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
                <Box mt={3}>
                    <FormControl className={classes.fullWidth}>
                        <FormLabel id={'zeek-host-label-' + props.index}>Hostname</FormLabel>
                        <Validator
                            disabled={(!props.perms.write || props.policySource === 'mc2')}
                            valueType="host"
                            errorText="Must be a valid host"
                            fullWidth
                            value={data.dest.host}
                            onChange={handleHost}
                        />
                    </FormControl>
                </Box>
                <Box mt={3}>
                    <FormControl className={classes.fullWidth}>
                        <FormLabel id={'zeek-port-label-' + props.index}>Port</FormLabel>
                        <Validator
                            disabled={(!props.perms.write || props.policySource === 'mc2')}
                            valueType="port"
                            errorText="Must be a valid port number"
                            fullWidth
                            value={data.dest.port}
                            onChange={handlePort}
                        />
                    </FormControl>
                </Box>
                {/* <Box mt={3}>
                    <Typography>Event Pruning Type:</Typography>
                    <RadioGroup row value={data.prune_type} onChange={handlePruneTypeChange}>
                        <FormControlLabel
                            disabled={(!props.perms.write || props.policySource === 'mc2')}
                            label="Inactive"
                            aria-label="inactive"
                            control={<Radio value='inactive' />} />
                        <FormControlLabel
                            disabled={(!props.perms.write || props.policySource === 'mc2')}
                            label="Remove"
                            aria-label="remove"
                            control={<Radio value='black' />} />
                        <FormControlLabel
                            disabled={(!props.perms.write || props.policySource === 'mc2')}
                            label="Add"
                            aria-label="add"
                            control={<Radio value='white' />} />
                    </RadioGroup>
                </Box>
                <Box mt={3}>
                    <FormControl className={classes.fullWidth}>
                        <FormLabel>Event Pruning Keys:</FormLabel>
                        <Validator
                            disabled={(!props.perms.write || props.policySource === 'mc2')}
                            allowEmpty
                            valueType="keyPairList"
                            errorText="Must be valid comma separated key pairs ex. bricata:dest_location"
                            fullWidth
                            value={data.prune_keys ? data.prune_keys.join(',') : []}
                            onChange={handlePruneKeysChange}
                        />
                    </FormControl>
                </Box> */}
            </Grid>
            <Grid item xs={6}>
                <Box>
                    <FormControlLabel
                        labelPlacement="start"
                        label="FIPS"
                        disabled={(!props.perms.write || props.policySource === 'mc2') || data.dest.proto !== 'TLS'}
                        control={
                            <Checkbox
                                checked={data.tls_fips}
                                name=""
                                onChange={handleFIPS}
                            />
                        }
                    />
                </Box>
                <Box mt={3}>
                    <FormControlLabel
                        labelPlacement="start"
                        label="CA Cert"
                        disabled={(!props.perms.write || props.policySource === 'mc2') || data.dest.proto !== 'TLS'}
                        control={
                            <Checkbox
                                checked={data.tls_cert.enable}
                                name=""
                                onChange={handleCACert}
                            />
                        }
                    />
                </Box>
                <Box mt={3}>
                    <input
                        id={'zeek-cert-file-' + props.index}
                        className={classes.hideFileInput}
                        type="file"
                        onChange={handleTLSContents}
                        disabled={(!props.perms.write || props.policySource === 'mc2') || !data.tls_cert.enable}
                    />
                    {(props.perms.write || props.policySource !== 'mc2') && <label htmlFor={'zeek-cert-file-' + props.index}>
                        <Button
                            variant="contained"
                            aria-label="upload cert"
                            color="primary"
                            component="span"
                            disabled={!data.tls_cert.enable}
                        >
                            Upload Cert
                        </Button>
                    </label>}
                </Box>
                <Box className={classes.certBox} display={data.tls_cert.file_name ? 'flex' : 'none'}>
                    {data.tls_cert.file_name}
                </Box>
            </Grid>
            <Grid item xs={12}>
                <Box className={classes.codeContainer} width="100%">
                    <CodeMirror
                        readOnly={!props.perms.write}
                        editorDidMount={(e) => { setFormatRef(e) }}
                        value={data.bro.format}
                        options={{
                            ...codemirrorOptions,
                            readOnly: (!props.perms.write || props.policySource === 'mc2'),
                        }}
                        onBeforeChange={(c, e, value) => { handleFormat(value) }}
                    />
                </Box>
            </Grid>
            <Grid item xs={12}>
                <Typography>Apply format preset:</Typography>
                <ButtonGroup disabled={(!props.perms.write || props.policySource === 'mc2')}>
                    <Button variant="contained" color="primary" aria-label="conn" onClick={() => { handleSetFormat('conn') }}>Conn</Button>
                    <Button variant="contained" color="primary" aria-label="dns" onClick={() => { handleSetFormat('dns') }}>DNS</Button>
                    <Button variant="contained" color="primary" aria-label="http" onClick={() => { handleSetFormat('http') }}>HTTP</Button>
                    <Button variant="contained" color="primary" aria-label="intel" onClick={() => { handleSetFormat('intel') }}>Intel</Button>
                    <Button variant="contained" color="primary" aria-label="notice" onClick={() => { handleSetFormat('notice') }}>Notice</Button>
                    <Button variant="contained" color="primary" aria-label="ssl" onClick={() => { handleSetFormat('ssl') }}>SSL</Button>
                    <Button variant="contained" color="primary" aria-label="weird" onClick={() => { handleSetFormat('weird') }}>Weird</Button>
                    <Button variant="contained" color="primary" aria-label="empty" onClick={() => { handleSetFormat('empty') }}>Empty</Button>
                </ButtonGroup>
            </Grid>
            <Filter
                filter={data.filterconf}
                ref={filterRef}
                onUpdate={handleUpdateFilter}
                onVerify={() => { handleVerify(props.name, props.index, false) }}
                policySource={props.policySource}
                perms={props.perms}
            />
            {(props.perms.write || props.policySource !== 'mc2') && <Grid item xs={12}>
                <Box mt={2}>
                    <Button
                        className={classes.buttonRight}
                        variant="contained"
                        color="primary"
                        aria-label="save"
                        onClick={() => { handleVerify(props.name, props.index, true) }}
                        disabled={!(checkValidity(dataForm.get()))}
                    >Verify</Button>
                    <Button className={classes.buttonRight} variant="contained" color="secondary" aria-label="cancel" onClick={() => { handleCancel(props.name, props.index) }}>Cancel</Button>
                </Box>
            </Grid>}
        </Grid>
    );
});

Zeek.propTypes = {
    data: PropTypes.object.isRequired,
    policySource: PropTypes.string,
    name: PropTypes.string.isRequired,
    index: PropTypes.number.isRequired,
    onCancel: PropTypes.func.isRequired,
    onVerify: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    onUpdateForm: PropTypes.func,
    perms: PropTypes.object,
};

export default Zeek;
